import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Search, X, ExternalLink } from 'lucide-react';
import { API } from '../App';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { devError } from '../utils/logger';
import { Dialog, DialogContent } from '../components/ui/dialog';
import SubmitInspirationDialog from '../components/SubmitInspirationDialog';

export default function InspirationPage() {
  const [meta, setMeta] = useState({ categories: [], tags: [], project_count: 0 });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeTag, setActiveTag] = useState(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [openSlug, setOpenSlug] = useState(null);
  const [openProject, setOpenProject] = useState(null);

  // Load meta once
  useEffect(() => {
    axios
      .get(`${API}/inspirations/_meta`)
      .then((r) => setMeta(r.data))
      .catch((e) => devError('inspiration meta', e));
  }, []);

  // Load list whenever filters change
  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (activeCategory) params.set('category', activeCategory);
    if (activeTag) params.set('tag', activeTag);
    if (search) params.set('search', search);
    axios
      .get(`${API}/inspirations?${params.toString()}`)
      .then((r) => setItems(r.data))
      .catch((e) => devError('inspiration list', e))
      .finally(() => setLoading(false));
  }, [activeCategory, activeTag, search]);

  // Load detail when modal opens
  useEffect(() => {
    if (!openSlug) {
      setOpenProject(null);
      return;
    }
    axios
      .get(`${API}/inspirations/${openSlug}`)
      .then((r) => setOpenProject(r.data))
      .catch((e) => devError('inspiration detail', e));
  }, [openSlug]);

  const clearFilters = () => {
    setActiveCategory(null);
    setActiveTag(null);
    setSearch('');
    setSearchInput('');
  };

  const hasFilter = activeCategory || activeTag || search;

  const subtitle = useMemo(() => {
    if (loading) return 'Loading…';
    const parts = [`${items.length} project${items.length === 1 ? '' : 's'}`];
    if (activeCategory) parts.push(`in ${activeCategory}`);
    if (activeTag) parts.push(`tagged ${activeTag}`);
    if (search) parts.push(`matching "${search}"`);
    return parts.join(' · ');
  }, [items.length, loading, activeCategory, activeTag, search]);

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="inspiration-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Hero */}
        <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-accent mb-2">Beautiful reuse</p>
            <h1 className="font-playfair text-4xl md:text-5xl font-bold mb-3">Inspiration Gallery</h1>
            <p className="text-muted-foreground max-w-2xl">
              Built architecture made from reclaimed material — railway sleepers, shipping containers,
              highway offcuts, windmill blades, salvaged brick. {meta.project_count} reference
              projects to fuel your next reuse-led design.
            </p>
          </div>
          <SubmitInspirationDialog
            meta={meta}
            onSubmitted={() => {
              // Refresh meta count once a submission lands (it'll reflect when approved).
              axios.get(`${API}/inspirations/_meta`).then((r) => setMeta(r.data)).catch(() => {});
            }}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
          {/* Sidebar filters */}
          <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setSearch(searchInput.trim());
              }}
              className="relative"
            >
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search projects, architects…"
                className="pl-9"
                data-testid="inspiration-search"
              />
            </form>

            {hasFilter && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs text-muted-foreground hover:text-accent inline-flex items-center gap-1"
                data-testid="inspiration-clear-filters"
              >
                <X className="w-3 h-3" /> Clear filters
              </button>
            )}

            <div>
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Categories</h3>
              <div className="flex flex-wrap gap-2 lg:flex-col lg:gap-1.5">
                {meta.categories.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setActiveCategory(activeCategory === c ? null : c)}
                    className={`text-left text-sm px-3 py-1.5 rounded-md transition-colors ${
                      activeCategory === c
                        ? 'bg-accent/20 text-accent border border-accent/40'
                        : 'border border-transparent text-foreground/80 hover:bg-muted/30 hover:text-accent'
                    }`}
                    data-testid={`inspiration-category-${c}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Tags</h3>
              <div className="flex flex-wrap gap-1.5">
                {meta.tags.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setActiveTag(activeTag === t ? null : t)}
                    className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
                      activeTag === t
                        ? 'bg-accent/20 text-accent border border-accent/40'
                        : 'border border-border/30 text-muted-foreground hover:text-accent hover:border-accent/30'
                    }`}
                    data-testid={`inspiration-tag-${t}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Grid */}
          <main>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-4" data-testid="inspiration-subtitle">
              {subtitle}
            </p>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 text-accent animate-spin" />
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-20 rounded-xl border border-border/20 bg-card/30">
                <p className="text-muted-foreground mb-4">No projects match those filters yet.</p>
                <Button onClick={clearFilters} variant="outline">Clear filters</Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="inspiration-grid">
                {items.map((p) => (
                  <button
                    key={p.project_id}
                    type="button"
                    onClick={() => setOpenSlug(p.slug)}
                    className="group relative rounded-xl overflow-hidden border border-border/30 bg-card/30 text-left hover:border-accent/40 transition-colors"
                    data-testid={`inspiration-card-${p.slug}`}
                  >
                    <div className="aspect-[4/3] overflow-hidden bg-muted/30">
                      {p.primary_image ? (
                        <img
                          src={p.primary_image}
                          alt={p.title}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full" />
                      )}
                      {p.image_count > 1 && (
                        <span className="absolute top-3 right-3 px-2 py-1 text-[10px] uppercase tracking-wider bg-black/60 text-white rounded">
                          {p.image_count} photos
                        </span>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-playfair text-lg leading-tight mb-1">{p.title}</h3>
                      {p.architect && (
                        <p className="text-xs text-muted-foreground mb-2">{p.architect}</p>
                      )}
                      <p className="text-xs text-foreground/70 line-clamp-3 mb-3">{p.excerpt}</p>
                      <div className="flex flex-wrap gap-1">
                        {p.tags.slice(0, 4).map((t) => (
                          <span key={t} className="text-[10px] px-2 py-0.5 rounded-full border border-border/30 text-muted-foreground">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Detail modal */}
      <Dialog open={!!openSlug} onOpenChange={(v) => !v && setOpenSlug(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-card border-border/40" data-testid="inspiration-detail">
          {openProject ? (
            <div className="max-h-[88vh] overflow-y-auto">
              {openProject.images.length > 0 && (
                <div className="bg-black">
                  <img
                    src={openProject.images[0].url}
                    alt={openProject.title}
                    className="w-full h-auto max-h-[60vh] object-contain"
                  />
                </div>
              )}
              <div className="p-6 md:p-8">
                <h2 className="font-playfair text-2xl md:text-3xl mb-2">{openProject.title}</h2>
                <p className="text-xs text-muted-foreground mb-4">
                  {openProject.architect && <span>Architect: <span className="text-foreground/80">{openProject.architect}</span></span>}
                  {openProject.architect && openProject.photographer && <span> · </span>}
                  {openProject.photographer && <span>Photo: <span className="text-foreground/80">{openProject.photographer}</span></span>}
                </p>
                {openProject.body && (
                  <p className="text-sm text-foreground/80 leading-relaxed mb-5">{openProject.body}</p>
                )}
                <div className="flex flex-wrap gap-1.5 mb-5">
                  {openProject.categories.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => { setOpenSlug(null); setActiveTag(null); setActiveCategory(c); }}
                      className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-accent/15 text-accent border border-accent/30 hover:bg-accent/25"
                    >
                      {c}
                    </button>
                  ))}
                  {openProject.tags.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => { setOpenSlug(null); setActiveCategory(null); setActiveTag(t); }}
                      className="text-[10px] px-2 py-1 rounded-full border border-border/30 text-muted-foreground hover:text-accent hover:border-accent/30"
                    >
                      #{t}
                    </button>
                  ))}
                </div>
                {openProject.source_url && (
                  <a
                    href={openProject.source_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-accent hover:underline"
                  >
                    Read more <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {openProject.images.length > 1 && (
                  <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {openProject.images.slice(1).map((img, idx) => (
                      <img
                        key={img.url || idx}
                        src={img.url}
                        alt=""
                        loading="lazy"
                        className="w-full h-40 object-cover rounded border border-border/30"
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 text-accent animate-spin" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
